// src/contexts/SupplierDashboardContext.jsx
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useUser } from './UserContext';

const ENV_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_BASE) ||
  process.env.REACT_APP_API_BASE ||
  '';
const CLEAN_ENV_BASE = ENV_BASE.replace(/\/+$/, '');
const FALLBACK_BASE = 'https://www.doroosonline.co.il';
const API_BASE = CLEAN_ENV_BASE || FALLBACK_BASE;
const url = (path) => `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;

const getStoredToken = () => {
  try {
    return localStorage.getItem('jwt') ?? localStorage.getItem('token') ?? null;
  } catch {
    return null;
  }
};

async function apiFetch(path) {
  const token = getStoredToken();
  const res = await fetch(url(path), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    mode: 'cors',
    cache: 'no-store',
    credentials: 'omit',
  });
  const text = await res.text();
  let data = {};
  try { data = text ? JSON.parse(text) : {}; } catch { /* ignore */ }

  if (!res.ok || data?.ok === false) {
    throw new Error(data?.error || `שגיאת שרת (${res.status})`);
  }
  return data;
}

const SupplierDashboardContext = createContext(null);

export const SupplierDashboardProvider = ({ children }) => {
  const { role, userType, showAlert, ready, isLogedin } = useUser();
  const isSupplier = role === 'supplier' || userType === 'supplier';

  const [overview, setOverview] = useState(null);
  const [publicCoursesSummary, setPublicCoursesSummary] = useState(null);
  const [upcomingLessons, setUpcomingLessons] = useState([]);
  const [months, setMonths] = useState([]);
  const [programNames, setProgramNames] = useState([]);
  const [registrationGrowth, setRegistrationGrowth] = useState([]);
  const [couponsTrend, setCouponsTrend] = useState([]);
  const [couponsByProgram, setCouponsByProgram] = useState([]);
  const [publicRegistrationGrowth, setPublicRegistrationGrowth] = useState([]);
  const [loading, setLoading] = useState(false);
  const [trendsLoading, setTrendsLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const fetchDashboard = useCallback(async () => {
    if (!isSupplier) return null;
    const startedAt = Date.now();
    setLoading(true);
    try {
      const data = await apiFetch('/api/supplier/dashboard/summary');
      setOverview(data?.overview || null);
      setPublicCoursesSummary(data?.publicCoursesSummary || null);
      setUpcomingLessons(Array.isArray(data?.upcomingLessons) ? data.upcomingLessons : []);
      setLoaded(true);
      return data;
    } catch (err) {
      showAlert?.(err.message || 'שגיאה בטעינת לוח הבקרה', 'warning');
      return null;
    } finally {
      // Same fix already applied to SupplierProgramsContext.jsx — a
      // request that resolves in a frame or two (fast server, cached
      // connection) flips loading true->false too quickly for a human
      // to ever actually see the page's initial SpinnerOverlay, even
      // though the state and the render are both working correctly.
      const MIN_VISIBLE_MS = 400;
      const elapsed = Date.now() - startedAt;
      const remaining = MIN_VISIBLE_MS - elapsed;
      if (remaining > 0) {
        await new Promise((resolve) => setTimeout(resolve, remaining));
      }
      setLoading(false);
    }
  }, [isSupplier, showAlert]);

  const fetchTrends = useCallback(async () => {
    if (!isSupplier) return null;
    const startedAt = Date.now();
    setTrendsLoading(true);
    try {
      const data = await apiFetch('/api/supplier/dashboard/trends');
      setMonths(Array.isArray(data?.months) ? data.months : []);
      setProgramNames(Array.isArray(data?.programNames) ? data.programNames : []);
      setRegistrationGrowth(Array.isArray(data?.registrationGrowth) ? data.registrationGrowth : []);
      setCouponsTrend(Array.isArray(data?.couponsTrend) ? data.couponsTrend : []);
      setCouponsByProgram(Array.isArray(data?.couponsByProgram) ? data.couponsByProgram : []);
      setPublicRegistrationGrowth(Array.isArray(data?.publicRegistrationGrowth) ? data.publicRegistrationGrowth : []);
      return data;
    } catch (err) {
      showAlert?.(err.message || 'שגיאה בטעינת מגמות', 'warning');
      return null;
    } finally {
      const MIN_VISIBLE_MS = 400;
      const elapsed = Date.now() - startedAt;
      const remaining = MIN_VISIBLE_MS - elapsed;
      if (remaining > 0) {
        await new Promise((resolve) => setTimeout(resolve, remaining));
      }
      setTrendsLoading(false);
    }
  }, [isSupplier, showAlert]);

  useEffect(() => {
    // Same gating as SupplierProgramsContext — wait for the session
    // check to actually finish before fetching anything, so this
    // never fires against a stale/expired token on first paint.
    if (!ready) return;

    if (!isLogedin) {
      setOverview(null);
      setPublicCoursesSummary(null);
      setUpcomingLessons([]);
      setMonths([]);
      setProgramNames([]);
      setRegistrationGrowth([]);
      setCouponsTrend([]);
      setCouponsByProgram([]);
      setPublicRegistrationGrowth([]);
      setLoaded(false);
      return;
    }

    if (!isSupplier) return;
    fetchDashboard();
    fetchTrends();
  }, [ready, isLogedin, isSupplier, fetchDashboard, fetchTrends]);

  const value = useMemo(
    () => ({
      overview,
      publicCoursesSummary,
      upcomingLessons,
      months,
      programNames,
      registrationGrowth,
      couponsTrend,
      couponsByProgram,
      publicRegistrationGrowth,
      loading,
      trendsLoading,
      loaded,
      fetchDashboard,
      fetchTrends,
    }),
    [
      overview, publicCoursesSummary, upcomingLessons,
      months, programNames, registrationGrowth, couponsTrend, couponsByProgram,
      publicRegistrationGrowth,
      loading, trendsLoading, loaded, fetchDashboard, fetchTrends,
    ]
  );

  return (
    <SupplierDashboardContext.Provider value={value}>
      {children}
    </SupplierDashboardContext.Provider>
  );
};

export const useSupplierDashboard = () => {
  const ctx = useContext(SupplierDashboardContext);
  if (!ctx) throw new Error('useSupplierDashboard must be used within a SupplierDashboardProvider');
  return ctx;
};